import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';
import { apiService } from '@/services/api';
import ReportHeader from '@/components/ReportHeader';
import { RefreshCw, Calendar } from 'lucide-react';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

// select removido (nenhum select ativo nesta tela)

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const HeatmapWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  padding: 12px 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 84px repeat(24, 36px);
  gap: 2px;
`;

const HeaderCell = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  padding: 4px 0;
`;

const DayLabel = styled.div`
  font-size: 12px;
  color: #111827;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  padding-right: 8px;
`;

const Cell = styled.div<{ color: string }>`
  width: 36px;
  height: 28px;
  border-radius: 4px;
  background: ${p => p.color};
  position: relative;
  cursor: default;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 12px;
  color: #6b7280;
`;

const Swatch = styled.div<{ color: string }>`
  width: 24px;
  height: 10px;
  border-radius: 2px;
  background: ${p => p.color};
  border: 1px solid rgba(0,0,0,0.05);
`;

type MatCell = { dow: number; hora: number; quantidade: number; total: number };

const diasNomes: Record<number, string> = {
  1: 'Domingo',
  2: 'Segunda',
  3: 'Terça',
  4: 'Quarta',
  5: 'Quinta',
  6: 'Sexta',
  7: 'Sábado'
};

const diasOrdem = [2,3,4,5,6,7,1]; // mostrar de Segunda a Domingo

function colorScaleBlue(value: number, max: number): string {
  if (!max || max <= 0 || value <= 0) return '#eef2ff'; // azul muito claro
  const t = Math.min(1, Math.max(0, value / max));
  // Interpolação manual entre #dbeafe (light) -> #3b82f6 (medium) -> #1e40af (dark) com easing leve
  const e = Math.pow(t, 0.8);
  const stops = [
    { r: 219, g: 234, b: 254 }, // #dbeafe
    { r: 59,  g: 130, b: 246 }, // #3b82f6
    { r: 30,  g: 64,  b: 175 }  // #1e40af
  ];
  const idx = e < 0.5 ? 0 : 1;
  const localT = e < 0.5 ? (e / 0.5) : ((e - 0.5) / 0.5);
  const a = stops[idx];
  const b = stops[idx + 1];
  const r = Math.round(a.r + (b.r - a.r) * localT);
  const g = Math.round(a.g + (b.g - a.g) * localT);
  const bch = Math.round(a.b + (b.b - a.b) * localT);
  return `rgb(${r}, ${g}, ${bch})`;
}

const RelatorioHeatmapVendas: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [dataInicio, setDataInicio] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [todasFiliais, setTodasFiliais] = useState<boolean>(true);
  // Configurações fixas solicitadas
  const FORMA_FIXA: 'todos' = 'todos';

  const queryKey = useMemo(() => (
    ['relatorio-heatmap', todasFiliais ? 'todas' : (filialSelecionada?.id || '-'), dataInicio, dataFim]
  ), [todasFiliais, filialSelecionada?.id, dataInicio, dataFim]);

  const { data, isFetching, isLoading, refetch } = useQuery({
    queryKey,
    enabled: todasFiliais || !!filialSelecionada?.id,
    queryFn: async () => {
      if (todasFiliais) {
        const res = await apiService.obterHeatmapVendasTodas({ dataInicio, dataFim, formaPagamento: FORMA_FIXA });
        return res?.data;
      }
      if (!filialSelecionada?.id) return null;
      const res = await apiService.obterHeatmapVendasFilial(filialSelecionada.id, { dataInicio, dataFim, formaPagamento: FORMA_FIXA });
      return res?.data;
    }
  });

  const matriz: MatCell[] = data?.matriz || [];
  const maxTotal: number = data?.maxTotal || 0;

  // Util: quantil simples (índice mais próximo para robustez e custo baixo)
  const quantile = (arr: number[], q: number) => {
    if (!arr || arr.length === 0) return 0;
    const idx = Math.max(0, Math.min(arr.length - 1, Math.floor(q * (arr.length - 1))));
    return arr[idx];
  };

  // Coleções de valores positivos para cap (p95) e decisão de log
  const valuesPosGlobal = useMemo(() => {
    const vals = matriz.map(c => Number(c.total) || 0).filter(v => v > 0).sort((a,b) => a-b);
    return vals;
  }, [matriz]);

  const uniqueCountGlobal = useMemo(() => new Set(valuesPosGlobal).size, [valuesPosGlobal]);
  const p95Global = useMemo(() => (valuesPosGlobal.length > 0 ? quantile(valuesPosGlobal, 0.95) : 0), [valuesPosGlobal]);

  const tooltipText = (c: MatCell) => {
    const q = Number(c.quantidade) || 0;
    const t = Number(c.total) || 0;
    const totalStr = t.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    return `${diasNomes[c.dow]} ${String(c.hora).padStart(2,'0')}:00\n${q} vendas • ${totalStr}`;
  };

  return (
    <Container>
      <ReportHeader
        title="Heatmap de Vendas por Dia x Hora"
        subtitle={`${todasFiliais ? 'Todas as Filiais' : (filialSelecionada?.nome || '')}`}
        rightActions={(
          <Acoes>
            <Botao onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw size={16} /> Atualizar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
        {/* Forma de Pagamento fixa: Todos */}
        {/* Métrica e Normalização fixas: Valor Total + Global */}
        <Acoes>
          <Botao onClick={() => refetch()} disabled={isLoading || isFetching}>
            <Calendar size={16} /> Aplicar
          </Botao>
        </Acoes>
      </Filtros>

      <HeatmapWrapper>
        <Grid>
          <HeaderCell></HeaderCell>
          {Array.from({ length: 24 }, (_, h) => (
            <HeaderCell key={`h-${h}`}>{String(h).padStart(2,'0')}h</HeaderCell>
          ))}

          {diasOrdem.map((dow) => (
            <React.Fragment key={`row-${dow}`}>
              <DayLabel>{diasNomes[dow]}</DayLabel>
              {Array.from({ length: 24 }, (_, h) => {
                const c = matriz.find(m => m.dow === dow && m.hora === h) || { dow, hora: h, quantidade: 0, total: 0 };
                const valNum = Number(c.total) || 0; // métrica fixa: valor total

                let color: string;
                // Normalização fixa: Global (com cap p95 e fallback log)
                const maxGlobalBase = maxTotal;
                const capGlobal = Math.max(1, p95Global || maxGlobalBase || 0);
                const useLog = uniqueCountGlobal <= 3;
                const v = useLog ? Math.log1p(valNum) : valNum;
                const m = useLog ? Math.log1p(capGlobal) : capGlobal;
                color = colorScaleBlue(v, m);
                return (
                  <Cell
                    key={`c-${dow}-${h}`}
                    color={color}
                    title={tooltipText(c)}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
        <Legend>
          <span>Baixo</span>
          <Swatch color={colorScaleBlue(0, 100)} />
          <Swatch color={colorScaleBlue(25, 100)} />
          <Swatch color={colorScaleBlue(50, 100)} />
          <Swatch color={colorScaleBlue(75, 100)} />
          <Swatch color={colorScaleBlue(100, 100)} />
          <span>Alto</span>
        </Legend>
      </HeatmapWrapper>
    </Container>
  );
};

export default RelatorioHeatmapVendas;


